import React from 'react';
import PropTypes from 'prop-types';
import differenceInMonths from 'date-fns/difference_in_months';
import differenceInHours from 'date-fns/difference_in_hours';
import differenceInDays from 'date-fns/difference_in_days';
import differenceInWeeks from 'date-fns/difference_in_weeks';
import differenceInYears from 'date-fns/difference_in_years';
import differenceInMinutes from 'date-fns/difference_in_minutes';
import isAfter from 'date-fns/is_after';
import subMonths from 'date-fns/sub_months';
import subHours from 'date-fns/sub_hours';
import subDays from 'date-fns/sub_days';
import subWeeks from 'date-fns/sub_weeks';
import subMinutes from 'date-fns/sub_minutes';
import subYears from 'date-fns/sub_years';
import startOfDay from 'date-fns/start_of_day';
import format from 'date-fns/format';
import * as Translations from '../../providers/i18n';
import { timeUntilFullMinute } from './helpers';

export const SAME_YEAR_DATETIME_FORMAT = 'MMM DD, h:mm A';

class Time extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentTime: new Date(),
            dateTime: new Date(props.dateTime)
        };
        this.updateTime = this.updateTime.bind(this);
    }

    componentDidMount() {
        if (this.props.type !== this.TYPE_RELATIVE) { return; }

        this.updateTime();
    }

    componentDidUpdate(prevProps) {
        if (this.props.dateTime !== prevProps.dateTime) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                dateTime: new Date(this.props.dateTime)
            });
        }
    }

    componentWillUnmount() {
        window.clearTimeout(this.updateTimeReference);
    }

    updateTime(timer = timeUntilFullMinute()) {
        this.updateTimeReference = setTimeout(() => {
            this.setState({ currentTime: new Date() });
            this.updateTime(this.MILLISECONDS_IN_MINUTE);
        }, timer);
    }

    relativeDate(i18n) {
        const relativeSuffix = this.props.relativeSuffix || '';

        if (this.isGivenDateBeforeUnit('m')) {
            return i18n.t('attachments_preview.datetime.just_now');
        }
        else if (this.isGivenDateBeforeUnit('h')) {
            return `${this.diffFromCurrentToGivenDate('m')}m ${relativeSuffix}`;
        }
        else if (this.isGivenDateBeforeUnit('d')) {
            return `${this.diffFromCurrentToGivenDate('h')}h ${relativeSuffix}`;
        }
        else if (this.isGivenDateBeforeUnit('w')) {
            return `${this.diffFromCurrentToGivenDate('d')}d ${relativeSuffix}`;
        }
        else if (this.isGivenDateBeforeUnit('M')) {
            const count = this.diffFromCurrentToGivenDate('w');
            return `${i18n.t('attachments_preview.datetime.week', { count })} ${relativeSuffix}`;
        }
        else if (this.isGivenDateBeforeUnit('y')) {
            const count = this.diffFromCurrentToGivenDate('M');
            return `${i18n.t('attachments_preview.datetime.month', { count })} ${relativeSuffix}`;
        } else {
            const count = this.diffFromCurrentToGivenDate('y');
            return `${i18n.t('attachments_preview.datetime.year', { count })} ${relativeSuffix}`;
        }
    }

    diffFromCurrentToGivenDate(unit) {
        const { dateTime, currentTime } = this.state;
        switch (unit) {
            case 'm':
                return differenceInMinutes(currentTime, dateTime);
            case 'M':
                return differenceInMonths(currentTime, dateTime);
            case 'h':
                return differenceInHours(currentTime, dateTime);
            case 'd':
                return differenceInDays(currentTime, dateTime);
            case 'w':
                return differenceInWeeks(currentTime, dateTime);
            case 'y':
                return differenceInYears(currentTime, dateTime);
            default:

                // not valid case
        }
    }

    isGivenDateBeforeUnit(unit) {
        const { dateTime, currentTime } = this.state;
        let subtracted;
        switch (unit) {
            case 'm':
                subtracted = subMinutes(currentTime, 1);
                break;
            case 'M':
                subtracted = subMonths(currentTime, 1);
                break;
            case 'h':
                subtracted = subHours(currentTime, 1);
                break;
            case 'd':
                subtracted = subDays(currentTime, 1);
                break;
            case 'w':
                subtracted = subWeeks(currentTime, 1);
                break;
            case 'y':
                subtracted = subYears(currentTime, 1);
                break;
            default:
                return false;
        }
        return isAfter(dateTime, subtracted);
    }

    isGivenDateAfterMidnight() {
        const { dateTime } = this.state;
        const midnight = startOfDay(new Date());

        return isAfter(dateTime, midnight);
    }

    absoluteDateAndTimeFormat(locale) {
        const { dateTime } = this.state;

        if (this.props.withoutHours) {
            return format(dateTime, 'MMM DD, YYYY', { locale });
        } else if (this.isGivenDateBeforeUnit('y')) {
            return format(dateTime, SAME_YEAR_DATETIME_FORMAT, { locale });
        } else if (this.isGivenDateAfterMidnight()) {
            return format(dateTime, 'h:mm A', { locale });
        } else {
            return format(dateTime, 'MMM DD, YYYY, h:mm A', { locale });
        }
    }

    formatDateTime(i18n, locale) {
        const type = this.props.type;
        if (!this.props.dateTime) { return ''; }

        switch (type) {
            case this.TYPE_RELATIVE:
                return this.relativeDate(i18n);
            default:
                return this.absoluteDateAndTimeFormat(locale);
        }
    }

    render() {
        const { i18n, locale } = this.props;

        return (
            <time dateTime={new Date(this.props.dateTime)}>
                {this.formatDateTime(i18n, locale)}
            </time>
        );
    }
}

Time.propTypes = {
    dateTime: PropTypes.number.isRequired,
    type: PropTypes.string,
    relativeSuffix: PropTypes.string,
    withoutHours: PropTypes.bool,
    i18n: PropTypes.object,
    locale: PropTypes.string
};

Time.contextTypes = {
    locale: PropTypes.string
};

Time.prototype.TYPE_RELATIVE = 'RELATIVE';
Time.prototype.MILLISECONDS_IN_MINUTE = 60 * 1000;

export default Translations.connect(Time);
